import { isDevelopment } from "../../config/env";

const LOG_PREFIX = "[GRI Branding Debug]";
const MAX_STORED_LOGS = 50;

export interface BrandingDebugEntry {
  at: string;
  message: string;
  payload?: Record<string, unknown>;
}

declare global {
  interface Window {
    /** Set `window.__GRI_BRANDING_DEBUG__ = true` to force logs in standalone production. */
    __GRI_BRANDING_DEBUG__?: boolean;
    /** Recent branding debug entries — inspect from iframe or parent DevTools. */
    __GRI_BRANDING_DEBUG_LOGS__?: BrandingDebugEntry[];
  }
}

function isEmbeddedInParentFrame(): boolean {
  return typeof window !== "undefined" && window.parent !== window;
}

export function isBrandingDebugEnabled(): boolean {
  if (typeof window === "undefined") return isDevelopment;
  return (
    isDevelopment ||
    window.__GRI_BRANDING_DEBUG__ === true ||
    isEmbeddedInParentFrame()
  );
}

function appendBrandingDebugLog(entry: BrandingDebugEntry): void {
  if (typeof window === "undefined") return;
  const existing = window.__GRI_BRANDING_DEBUG_LOGS__ ?? [];
  window.__GRI_BRANDING_DEBUG_LOGS__ = [...existing, entry].slice(-MAX_STORED_LOGS);
}

export function brandingDebug(
  message: string,
  payload?: Record<string, unknown>,
): void {
  if (!isBrandingDebugEnabled()) return;

  const entry: BrandingDebugEntry = {
    at: new Date().toISOString(),
    message,
    ...(payload !== undefined ? { payload } : {}),
  };
  appendBrandingDebugLog(entry);

  // console.info is less likely to be filtered than console.log in some setups.
  if (payload === undefined) {
    console.info(LOG_PREFIX, message);
    return;
  }
  console.info(LOG_PREFIX, message, payload);
}

/** One-time banner when GRI runs inside URF iframe (helps find the right DevTools context). */
export function brandingDebugEmbeddedBanner(): void {
  if (!isEmbeddedInParentFrame()) return;

  let parentSameOrigin = false;
  try {
    parentSameOrigin = window.parent.location.origin === window.location.origin;
  } catch {
    parentSameOrigin = false;
  }

  brandingDebug(
    "GRI running inside iframe — open DevTools → Console → select this iframe context, or run window.__GRI_BRANDING_DEBUG_LOGS__",
    {
      iframeLocation: window.location.href,
      parentSameOrigin,
    },
  );
}
